'use client';

import { FC, useState } from 'react';
import Link from 'next/link';
import { format } from 'date-fns';
import { simpleDateToDate } from '@puzzlio/core';
import { unreadPuzzles } from '@puzzlio/core/comments';
import { FlatButton, Popover, PopoverContent, PopoverTrigger } from '@puzzlio/ui';
import { COUNT_BUBBLE, countBubbleBaseline, countBubbleFontSize } from '@puzzlio/ui/spec/countBubble';
import { GAME_CONFIG } from '@/app/games/config';
import { useCommentReplies } from '@/app/hooks/useComments';
import { cn } from '@/app/lib/utils';

/** The count in a speech bubble; see spec/countBubble. */
const CountBubble: FC<{ count: number }> = ({ count }) => {
  const fontSize = countBubbleFontSize(count);
  const { viewBox, path, strokeWidth, centerX } = COUNT_BUBBLE;
  return (
    <svg viewBox={`0 0 ${viewBox} ${viewBox}`} className="size-6" fill="none" aria-hidden>
      <path d={path} stroke="currentColor" strokeWidth={strokeWidth} strokeLinejoin="round" />
      <text
        x={centerX}
        y={countBubbleBaseline(fontSize)}
        textAnchor="middle"
        fill="currentColor"
        className="font-quicksand font-bold"
        fontSize={fontSize}
      >
        {count}
      </text>
    </svg>
  );
};

/**
 * The header's speech bubble, counting the puzzles with replies to the player they haven't read. Opens a list of
 * them, each linking to its comments. Absent while there's nothing unread.
 */
export const CommentRepliesButton: FC<{ className?: string }> = ({ className }) => {
  const [open, setOpen] = useState(false);
  const puzzles = unreadPuzzles(useCommentReplies().data ?? []);
  if (puzzles.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FlatButton
          size="mobileIcon"
          className={cn('lg:size-10 lg:border-2 lg:p-1.5', className)}
          aria-label={`New replies on ${puzzles.length} ${puzzles.length === 1 ? 'puzzle' : 'puzzles'}`}
          data-testid="comment-replies-button"
        >
          <CountBubble count={puzzles.length} />
        </FlatButton>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        collisionPadding={16}
        className="min-h-0 w-max max-w-[calc(100vw-32px)] min-w-0 px-0 py-2"
        data-testid="comment-replies-list"
      >
        <p className="font-quicksand px-5 py-2 text-sm font-bold text-black/60">New replies to you</p>
        <ul>
          {puzzles.map(({ game, date, count }) => {
            const { label, icon: Icon, commentsURL } = GAME_CONFIG[game];
            return (
              <li key={`${game}/${date}`}>
                <Link
                  href={commentsURL(date)}
                  prefetch={false}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-black/5"
                  data-testid="comment-replies-item"
                >
                  <Icon className="size-9 shrink-0" />
                  <span className="font-quicksand flex min-w-0 flex-col leading-tight">
                    <span className="text-base font-bold text-black">
                      {label} · {format(simpleDateToDate(date), 'EEE, MMM d')}
                    </span>
                    <span className="text-sm font-semibold text-black/60">
                      {count} new {count === 1 ? 'reply' : 'replies'}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
